import React, { useEffect, useState, useRef } from "react";
import * as echarts from "echarts";
import axios from "axios";
import "./geomap.scss";
import "bootstrap/dist/css/bootstrap.min.css";
import nutsDatasetMap from "../../utils/nutsDatasetMap.json";
import { useDatasets } from "../../hooks/useDatasets";

const AirGeoMap = ({ chartName, data, category, label }) => {
  const chartRef = useRef(null);
  const [selectedData, setSelectedData] = useState([]);
  const [nutsLevel, setNutsLevel] = useState("NUTS3"); // Default to NUTS3
  const [healthScenario, setHealthScenario] = useState(
    "WHO_2021_AQG_Scen_Base"
  ); // Default scenario
  const [airPollutant, setAirPollutant] = useState("O3"); // Default pollutant
  const [pollutantParameter, setPollutantParameter] = useState(
    "Air Pollution Population Weighted Average [ug/m3]"
  ); // Default parameter
  const [geoJsonUrl, setGeoJsonUrl] = useState(
    `${process.env.PUBLIC_URL}/NUTS_RG_20M_2021_3035_N3_v2.geojson`
  );
  const { getSpecificDataset } = useDatasets();
  const [dataUseInMap, setDataUseInMap] = useState(data);
  const urlMap = {
    NUTS1: `${process.env.PUBLIC_URL}/NUTS_RG_20M_2021_3035_N1_v2.geojson`,
    NUTS2: `${process.env.PUBLIC_URL}/NUTS_RG_20M_2021_3035_N2_v2.geojson`,
    NUTS3: `${process.env.PUBLIC_URL}/NUTS_RG_20M_2021_3035_N3_v2.geojson`,
  };

  // we need to fetch data for the map when the nut level changes
  useEffect(() => {
    const fetchupdateData = async () => {
      if (nutsLevel !== "NUTS3") {
        const key = Object.keys(nutsDatasetMap).find(function (k) {
          return k === label;
        });
        if (!key) throw new Error("Dataset not found in mapping.");
        const year = key.split(" ")[2];
        const fullTargetKey = `Air Pollutants ${year} ${nutsLevel}`;
        const datasetId = nutsDatasetMap[key][fullTargetKey];
        const updatedData = await getSpecificDataset(datasetId);
        setDataUseInMap(updatedData.elements);
      } else {
        setDataUseInMap(data);
      }
    };
    fetchupdateData();
    setGeoJsonUrl(urlMap[nutsLevel]);
  }, [nutsLevel, healthScenario]);

  useEffect(() => {
    const fetchMapData = async () => {
      if (!chartRef.current) {
        console.error("Chart container is missing!");
        return;
      }
      try {
        const response = await axios.get(geoJsonUrl);
        const geoJson = response.data;

        if (!geoJson || !geoJson.features || geoJson.features.length === 0) {
          console.error("Invalid geoJson data.");
          return;
        }
        echarts.registerMap("selectedGeoJson", geoJson);
        geoJson.features.forEach((feature) => {
          feature.properties.name = feature.properties.NUTS_ID;
        });
        const filteredData = dataUseInMap.filter(
          (item) =>
            item["Health Risk Scenario"] === healthScenario &&
            item["Air Pollutant"] === airPollutant
        );

        const firstRegion = geoJson.features.find((feature) =>
          filteredData.some(
            (item) => item[nutsLevel] === feature.properties.name
          )
        );

        const initialCenter = firstRegion
          ? firstRegion.geometry.coordinates[0][0]
          : [10.0, 50.0];
        const initialZoom = firstRegion ? 5 : 4;

        const groupedData = filteredData.reduce((acc, item) => {
          const key = item[nutsLevel];
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(item);
          return acc;
        }, {});

        const seriesData = Object.keys(groupedData).map((key) => {
          const aggregatedValue =
            groupedData[key].reduce(
              (acc, item) => acc + parseFloat(item[pollutantParameter]),
              0
            ) / groupedData[key].length;

          return {
            name: key,
            value: aggregatedValue,
            items: groupedData[key],
          };
        });

        // Filter out invalid or non-numeric values from seriesData
        const validValues = seriesData
          .map((item) => item?.value)
          .filter(
            (value) =>
              typeof value === "number" && !isNaN(value) && isFinite(value)
          );

        // Calculate minVal and maxVal only if validValues is not empty
        const minVal = validValues.length > 0 ? Math.min(...validValues) : 0;
        const maxVal = validValues.length > 0 ? Math.max(...validValues) : 0;

        if (chartRef.current) {
          echarts.dispose(chartRef.current); // Dispose of the existing chart instance
        }
        const myChart = echarts.init(chartRef.current);
        myChart.showLoading();

        myChart.setOption({
          tooltip: {
            trigger: "item",
            formatter: (params) =>
              `${params.name}: ${params.value?.toFixed(2) || "N/A"}`,
          },
          visualMap: {
            left: "right",
            min: minVal,
            max: maxVal,
            inRange: {
              color: [
                "#1a9850",
                "#66bd63",
                "#a6d96a",
                "#d9ef8b",
                "#ffffbf",
                "#fee08b",
                "#fdae61",
                "#f46d43",
                "#d73027",
              ],
            },
            text: ["High", "Low"],
            calculable: true,
          },
          toolbox: {
            feature: {
              dataView: { readOnly: false },
              restore: {},
              saveAsImage: {},
            },
          },
          series: [
            {
              name: "Regions",
              type: "map",
              map: "selectedGeoJson",
              roam: true,
              center: initialCenter,
              zoom: initialZoom,
              data: seriesData,
            },
          ],
        });

        myChart.hideLoading();

        myChart.on("click", function (params) {
          if (params.data && params.data.items) {
            setSelectedData(params.data.items);
            console.log("Clicked data:", params.data.items);
          }
        });
      } catch (error) {
        console.error("Error fetching map data:", error);
      }
    };

    fetchMapData();
  }, [
    chartName,
    category,
    geoJsonUrl,
    nutsLevel,
    healthScenario,
    airPollutant,
    pollutantParameter,
    dataUseInMap,
  ]);

  const handelChangeNutsLevel = (e) => {
    e.preventDefault();
    setNutsLevel(e.target.value);
    setGeoJsonUrl(urlMap[nutsLevel]);
  };

  return (
    <>
      {/* NUTS Level Dropdown */}
      <div className="container col-12">
        <div className="row">
          <div style={{ marginBottom: "10px", width: "25%" }}>
            <label htmlFor="nutsLevel" style={{ marginRight: "10px" }}>
              Select Region Level:
            </label>
            <select
              id="nutsLevel"
              value={nutsLevel}
              onChange={handelChangeNutsLevel}
              className="form-select"
            >
              <option value="NUTS1">NUTS-1 Levels Regions</option>
              <option value="NUTS2">Large Administrative Regions</option>
              <option value="NUTS3">Small Administrative Regions</option>
            </select>
          </div>

          {/* Health Risk Scenario Dropdown */}
          <div style={{ marginBottom: "10px", width: "25%" }}>
            <label htmlFor="healthScenario" style={{ marginRight: "10px" }}>
              Select Health Risk Scenario:
            </label>
            <select
              id="healthScenario"
              value={healthScenario}
              onChange={(e) => setHealthScenario(e.target.value)}
              className="form-select"
            >
              <option value="WHO_2021_AQG_Scen_Base">
                WHO 2021 AQG Scenario Base{" "}
              </option>
              <option value="WHO_2021_AQG_Scen_Sensitivity_2">
                WHO_2021_AQG_Scen_Sensitivity_2{" "}
              </option>
            </select>
          </div>

          {/* Air Pollutant Dropdown */}
          <div style={{ marginBottom: "10px", width: "25%" }}>
            <label htmlFor="airPollutant" style={{ marginRight: "10px" }}>
              Select Air Pollutant:
            </label>
            <select
              id="airPollutant"
              value={airPollutant}
              onChange={(e) => setAirPollutant(e.target.value)}
              className="form-select"
            >
              <option value="O3">Ozone (O3)</option>

              <option value="NO2">Nitrogen Dioxide (NO2)</option>
              <option value="PM10">Particulate Matter (PM10)</option>
              <option value="PM2.5">Fine Particulate Matter (PM2.5)</option>
            </select>
          </div>

          {/* Pollutant Parameter Dropdown */}
          <div style={{ marginBottom: "20px", width: "25%" }}>
            <label htmlFor="pollutantParameter" style={{ marginRight: "10px" }}>
              Select Pollutant Parameter:
            </label>
            <select
              id="pollutantParameter"
              value={pollutantParameter}
              onChange={(e) => setPollutantParameter(e.target.value)}
              className="form-select"
            >
              <option value="Air Pollution Population Weighted Average [ug/m3]">
                Population Weighted Avg
              </option>
              <option value="Premature Deaths">Premature Deaths</option>
              <option value="Years Of Life Lost">Years of Life Lost</option>
            </select>
          </div>
        </div>
      </div>

      <div
        id={chartName}
        ref={chartRef}
        style={{
          width: "100%",
          height: "400px",
          minWidth: "300px",
          minHeight: "200px",
        }}
      ></div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {selectedData.length > 0 && (
          <div
            style={{
              marginTop: "20px",
              width: "100%",
              display: "flex",
              justifyContent: "left",
            }}
          >
            <div className="table-container">
              <table className="table table-bordered table-striped">
                <thead>
                  <tr>
                    {Object.keys(selectedData[0]).map((key) => (
                      <th key={key}>{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selectedData.map((item, index) => (
                    <tr key={index}>
                      {Object.values(item).map((value, i) => (
                        <td key={i}>{value}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AirGeoMap;
